/**
 * Shared My Work (Zoho) live API helpers — timesheet + dashboard probes.
 * Leave PTO helpers remain in leaveApi.js.
 */

async function parseError(res) {
  const body = await res.json().catch(() => ({}));
  const detail = body.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
  }
  return res.statusText || "Request failed";
}

function authResult(res) {
  if (res.status === 401 || res.status === 403) {
    return { ok: false, auth: false, data: null };
  }
  return null;
}

/** Lightweight timesheet header payload (needs timekeeping.entry). */
export async function fetchMyQuickStatus() {
  const res = await fetch("/api/timekeeping/my-quick-status", {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return denied;
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Team novelties for My Day (reports depth≤2 + peers). */
export async function fetchTeamNovelties() {
  const res = await fetch("/api/timekeeping/team-novelties", {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: { items: [] } };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Manager timesheet approval queue (needs timekeeping.approve). */
export async function fetchTimesheetApprovalQueue(scope = "team") {
  const qs =
    scope === "org" ? "?scope=org" : scope === "team" ? "?scope=team" : "";
  const res = await fetch(`/api/timekeeping/approval-queue${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function fetchApprovalsPendingCount(scope = "team") {
  const qs = scope === "org" ? "?scope=org" : "";
  const res = await fetch(`/api/timekeeping/approvals/pending-count${qs}`, {
    credentials: "include",
  });
  if (!res.ok) {
    return {
      ok: false,
      auth: res.status === 401 || res.status === 403,
      count: 0,
      leave: 0,
      timesheets: 0,
    };
  }
  const data = await res.json();
  const leave = Number(data.leave) || 0;
  const timesheets = Number(data.timesheets) || 0;
  return {
    ok: true,
    auth: true,
    count: Number(data.count) || leave + timesheets,
    leave,
    leaveHr: Number(data.leave_hr) || leave,
    leaveManager: Number(data.leave_manager) || 0,
    timesheets,
    timesheetsManager: Number(data.timesheets_manager) || timesheets,
    data,
  };
}

export async function fetchApprovalHistory(scope = "team", year = new Date().getFullYear()) {
  const params = new URLSearchParams({ year: String(year) });
  if (scope === "org") params.set("scope", "org");
  const res = await fetch(`/api/timekeeping/approvals/history?${params}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: { leave: [], timesheets: [], year } };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function fetchTimesheetEntries(timesheetId) {
  const res = await fetch(`/api/timekeeping/timesheets/${timesheetId}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  const data = await res.json();
  return data.entries || data.time_entries || [];
}

/** Manager team hours for the current (or given) period, including drafts. */
export async function fetchTeamTimesheetStatus(periodId = null, scope = "direct") {
  const params = new URLSearchParams();
  if (periodId) params.set("period_id", String(periodId));
  if (scope === "all") params.set("scope", "all");
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`/api/timekeeping/team-status${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/**
 * Leader team daily-log + timesheet approve compliance (default since 2026-10-01).
 * Needs timekeeping.approve (or admin). Empty scope returns empty team payload.
 */
export async function fetchTeamCompliance({
  scope = "direct",
  fromDate = null,
  toDate = null,
} = {}) {
  const params = new URLSearchParams();
  if (scope === "all") params.set("scope", "all");
  if (fromDate) params.set("from_date", String(fromDate));
  if (toDate) params.set("to_date", String(toDate));
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`/api/timekeeping/team-compliance${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Pay periods for the manager's cadence (period picker on Approvals / Approve Timesheets). */
export async function fetchTimesheetPeriods(year = new Date().getFullYear()) {
  const res = await fetch(`/api/timekeeping/periods?year=${year}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  const data = await res.json();
  return { ok: true, auth: true, data: Array.isArray(data) ? data : [] };
}

export async function fetchCurrentTimesheetPeriod() {
  const res = await fetch("/api/timekeeping/periods/current", {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function approveTimesheet(timesheetId) {
  const res = await fetch(`/api/timekeeping/timesheets/${timesheetId}/approve`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function rejectTimesheet(timesheetId, reason = "Rejected by manager") {
  const res = await fetch(`/api/timekeeping/timesheets/${timesheetId}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

/** Server-persisted Daily Attendance days for a range (needs timekeeping.entry). */
export async function fetchAttendancePlan(startIso, endIso, personnelId = null) {
  const params = new URLSearchParams({ start: startIso, end: endIso });
  if (personnelId) params.set("personnel_id", String(personnelId));
  const res = await fetch(`/api/timekeeping/attendance/plan?${params}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: [], meta: null };
  if (!res.ok) throw new Error(await parseError(res));
  const body = await res.json();
  return {
    ok: true,
    auth: true,
    data: Array.isArray(body.days) ? body.days : [],
    meta: {
      personnel_id: body.personnel_id || null,
      display_name: body.display_name || null,
      can_edit: body.can_edit !== false,
      locks: body.locks || { full_day_pto: [], telework: {} },
    },
  };
}

/** Save one Daily Attendance day (at_office / home_office / tdy). */
export async function saveAttendanceDay(iso, status, personnelId = null) {
  const qs = personnelId
    ? `?personnel_id=${encodeURIComponent(String(personnelId))}`
    : "";
  const res = await fetch(`/api/timekeeping/attendance/plan/${iso}${qs}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

/** HR: set one day for all active Colombia personnel (mandatory At office or Home office). */
export async function bulkSetAttendanceDay(iso, status = "at_office") {
  const res = await fetch(
    `/api/timekeeping/attendance/plan/${encodeURIComponent(String(iso))}/bulk`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    }
  );
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Team attendance for one day (direct|all = reporting tree; org = all CO, HR only). */
export async function fetchAttendanceTeam(workDateIso = null, scope = "all") {
  const params = new URLSearchParams();
  if (workDateIso) params.set("work_date", String(workDateIso));
  if (scope) params.set("scope", String(scope));
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`/api/timekeeping/attendance/team${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** HR rules for attendance email exclusions + identity merges. */
export async function fetchAttendanceReportRules() {
  const res = await fetch(`/api/timekeeping/attendance/report-rules`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function saveAttendanceReportRules(custom) {
  const res = await fetch(`/api/timekeeping/attendance/report-rules`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(custom),
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Team attendance Mon–Sun week (``start`` any day in the week). */
export async function fetchAttendanceTeamWeek(startIso = null, scope = "all") {
  const params = new URLSearchParams();
  if (startIso) params.set("start", String(startIso));
  if (scope) params.set("scope", String(scope));
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`/api/timekeeping/attendance/team/week${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: null };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/**
 * Branded Colombia attendance roster PDF (logo, headers, notes column).
 * Opens in a new tab for print; also triggers a download.
 * @param {{ mode?: "day"|"week", workDateIso?: string|null, startIso?: string|null, scope?: string }} opts
 */
export async function downloadAttendanceRosterPdf({
  mode = "day",
  workDateIso = null,
  startIso = null,
  scope = "org",
} = {}) {
  const params = new URLSearchParams();
  params.set("mode", mode === "week" ? "week" : "day");
  if (scope) params.set("scope", String(scope));
  if (mode === "week") {
    if (startIso) params.set("start", String(startIso).slice(0, 10));
  } else if (workDateIso) {
    params.set("work_date", String(workDateIso).slice(0, 10));
  }
  const res = await fetch(
    `/api/timekeeping/attendance/team/pdf?${params.toString()}`,
    { credentials: "include" }
  );
  if (res.status === 401 || res.status === 403) {
    throw new Error("You do not have permission to print the attendance roster.");
  }
  if (!res.ok) throw new Error(await parseError(res));
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(
    res.headers.get("Content-Disposition"),
    mode === "week"
      ? `attendance_week_${String(startIso || "week").slice(0, 10)}.pdf`
      : `attendance_roster_${String(workDateIso || "day").slice(0, 10)}.pdf`
  );
  const url = URL.createObjectURL(blob);
  // Open for Ctrl+P / browser print; download keeps a local copy.
  const printWin = window.open(url, "_blank", "noopener,noreferrer");
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the new tab has had time to load the blob.
  window.setTimeout(() => {
    if (!printWin || printWin.closed) URL.revokeObjectURL(url);
    else window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, 30_000);
  return { ok: true, filename };
}

export async function fetchHolidays({ year, jurisdiction } = {}) {
  const params = new URLSearchParams();
  if (year != null) params.set("year", String(year));
  // my-holidays derives jurisdiction from Personnel; qs helps /holidays fallback
  if (jurisdiction) params.set("jurisdiction", jurisdiction);
  const qs = params.toString() ? `?${params}` : "";

  // When an explicit jurisdiction is passed (HR review / on-behalf), never use
  // my-holidays — that endpoint always returns the logged-in user's country.
  if (jurisdiction) {
    const res = await fetch(`/api/timekeeping/holidays${qs}`, {
      credentials: "include",
    });
    const denied = authResult(res);
    if (denied) return { ...denied, data: [] };
    if (!res.ok) return { ok: false, auth: true, data: [] };
    let data = await res.json();
    if (Array.isArray(data)) {
      const want = String(jurisdiction).toUpperCase();
      data = data.filter(
        (h) => String(h.jurisdiction || "").toUpperCase() === want
      );
    }
    return { ok: true, auth: true, data };
  }

  // Self-service: same holiday_calendar rows as the Holidays admin page
  const mine = await fetch(`/api/timekeeping/my-holidays${qs}`, {
    credentials: "include",
  });
  const mineDenied = authResult(mine);
  if (!mineDenied && mine.ok) {
    return { ok: true, auth: true, data: await mine.json() };
  }
  if (mineDenied) {
    const res = await fetch(`/api/timekeeping/holidays${qs}`, {
      credentials: "include",
    });
    const denied = authResult(res);
    if (denied) return { ...denied, data: [] };
    if (!res.ok) return { ok: false, auth: true, data: [] };
    return { ok: true, auth: true, data: await res.json() };
  }
  // my-holidays returned a non-auth error — try once without year filter
  if (!mine.ok && year != null) {
    return fetchHolidays({});
  }
  return { ok: false, auth: true, data: [] };
}

/** Fetch holiday_calendar rows for multiple years (attendance planning window). */
export async function fetchHolidaysForYears(years = [], jurisdiction) {
  const unique = [...new Set((years || []).filter((y) => y != null))];
  if (unique.length === 0) {
    return fetchHolidays({ jurisdiction });
  }
  const results = await Promise.all(
    unique.map((year) => fetchHolidays({ year, jurisdiction }))
  );
  const byKey = new Map();
  let ok = false;
  let auth = false;
  for (const r of results) {
    if (r.auth) auth = true;
    if (r.ok) ok = true;
    for (const row of r.data || []) {
      const key = `${row.id || ""}:${holidayDateIso(row)}:${row.name || ""}`;
      byKey.set(key, row);
    }
  }
  return { ok, auth, data: [...byKey.values()] };
}

/** Effective holiday ISO date (observed Monday move for CO when set). */
export function holidayDateIso(row) {
  return String(
    row?.observed_date || row?.holiday_date || row?.date || ""
  ).slice(0, 10);
}

/** Build a Set of YYYY-MM-DD holiday dates from API rows. */
export function holidaysToDateSet(rows = []) {
  const set = new Set();
  for (const h of rows || []) {
    const iso = holidayDateIso(h);
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) set.add(iso);
  }
  return set;
}

/** Map YYYY-MM-DD → holiday name for calendar tooltips. */
export function holidaysToNameMap(rows = []) {
  const map = new Map();
  for (const h of rows || []) {
    const iso = holidayDateIso(h);
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      map.set(iso, h.name || h.label || "Holiday");
    }
  }
  return map;
}

/** Map holiday_calendar rows → upcoming radar items (MM/DD/YY labels). */
export function mapUpcomingHolidays(rows = [], { limit = 4 } = {}) {
  if (!Array.isArray(rows)) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return rows
    .map((h) => {
      const iso = holidayDateIso(h);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
      const dt = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(dt.getTime()) || dt < today) return null;
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      const yy = String(dt.getFullYear()).slice(-2);
      return {
        name: h.name || h.label || "Holiday",
        date: `${mm}/${dd}/${yy}`,
        sort: iso,
        jurisdiction: h.jurisdiction || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.sort.localeCompare(b.sort))
    .slice(0, limit);
}

/** Personal log compliance (missed days + daily draft window). */
export async function fetchMyLogCompliance() {
  const res = await fetch("/api/timekeeping/my-log-compliance", {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return denied;
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Today's time entries for allocation bars. */
export async function fetchEntriesByDate(workDate) {
  const params = new URLSearchParams({ work_date: workDate });
  const res = await fetch(`/api/timekeeping/entries/by-date?${params}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Format a Date as YYYY-MM-DD in America/Chicago. */
export function centralDateIso(date = new Date()) {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/**
 * Server clock from log_window.now (API), else browser time.
 * Prefer this over `new Date()` for cutoff / “today” decisions.
 */
export function serverNowFromWindow(windowPayload) {
  if (windowPayload?.now) {
    const parsed = new Date(windowPayload.now);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

/** Server “today” in CT — prefers log_window.work_date from my-log-compliance. */
export function serverTodayIso(windowPayload) {
  const fromWindow = String(windowPayload?.work_date || "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromWindow)) return fromWindow;
  return centralDateIso(serverNowFromWindow(windowPayload));
}

/** Format clock stamp from server window (falls back to browser). */
export function formatCentralClock(windowPayload) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Chicago",
      timeZoneName: "short",
    }).format(serverNowFromWindow(windowPayload));
  } catch {
    return "";
  }
}

/**
 * Client-side helper: is daily log open for *today*.
 * Prefer server `log_window` from my-log-compliance / my-quick-status.
 * Draft hours are allowed all day; 4 PM is certify/submit only.
 */
export function isDailyLogOpenNow(windowPayload) {
  if (windowPayload && typeof windowPayload.allowed === "boolean") {
    return Boolean(windowPayload.allowed);
  }
  return true;
}

/**
 * Whether a YYYY-MM-DD day may receive draft entries in the week grid.
 * Past days (including Sunday travel): always (catch-up). Today: always
 * (log as you work). Future: locked unless approved/completed leave
 * (including Zoho import) covers that day. Certify/submit still uses 4 PM.
 *
 * @param {string} workDateIso
 * @param {object} [windowPayload]
 * @param {Set<string>|string[]|Map<string, unknown>} [leaveDays]
 */
export function canFillWorkDate(workDateIso, windowPayload, leaveDays) {
  if (!workDateIso) return false;
  const dt = new Date(`${workDateIso}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return false;

  const todayIso = serverTodayIso(windowPayload);

  if (workDateIso < todayIso) return true;
  if (workDateIso > todayIso) {
    if (!leaveDays) return false;
    if (typeof leaveDays.has === "function") return leaveDays.has(workDateIso);
    return Array.isArray(leaveDays) && leaveDays.includes(workDateIso);
  }
  return isDailyLogOpenNow(windowPayload);
}

export function formatMissedDateLabel(isoDate) {
  if (!isoDate) return "";
  const dt = new Date(`${isoDate}T12:00:00`);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yy = String(dt.getFullYear()).slice(-2);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return `${days[dt.getDay()]} ${mm}/${dd}/${yy}`;
}

export function mapTimesheetQueueToItems(queue = []) {
  if (!Array.isArray(queue)) return [];
  return queue.map((ts) => {
    const person =
      ts.personnel_name ||
      ts.employee_name ||
      ts.display_name ||
      "Employee";
    const hours = Number(ts.total_hours ?? ts.period_hours ?? 0);
    const period = ts.period || null;
    const fmtDay = (iso) => {
      const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return "";
      const dt = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00`);
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `${days[dt.getDay()]} ${m[2]}/${m[3]}/${m[1].slice(2)}`;
    };
    let periodDetail =
      period?.period_label || ts.period_label || "Current period";
    if (period?.period_start && period?.period_end) {
      periodDetail = `${fmtDay(period.period_start)} – ${fmtDay(
        period.period_end
      )}`;
    }
    return {
      id: ts.id,
      kind: "timesheet",
      title: `Timesheet — ${Number.isFinite(hours) ? hours.toFixed(1) : "0.0"}h`,
      person,
      detail: periodDetail,
      waiting: "manager",
      status: "pending",
      live: true,
      raw: ts,
      directHours: Number(ts.direct_hours ?? 0),
      indirectHours: Number(ts.indirect_hours ?? 0),
      leaveHours: Number(ts.leave_hours ?? 0),
      totalHours: Number.isFinite(hours) ? hours : 0,
    };
  });
}

export function mapTimesheetHistoryToItems(queue = []) {
  if (!Array.isArray(queue)) return [];
  return mapTimesheetQueueToItems(queue).map((item) => {
    const status = String(item.raw?.status || "approved").toLowerCase();
    const decidedAt = item.raw?.approved_at || item.raw?.rejected_at || null;
    const statusLabel = status === "rejected" ? "Returned" : "Approved";
    return {
      ...item,
      status,
      viewOnly: true,
      decidedAt,
      title: `Timesheet — ${Number.isFinite(item.totalHours) ? item.totalHours.toFixed(1) : "0.0"}h · ${statusLabel}`,
    };
  });
}

/** List current user's timesheets (all statuses) for the Timesheets tab. */
export async function fetchMyTimesheets() {
  const res = await fetch("/api/timekeeping/my-timesheets", {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return denied;
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

/** Parse Content-Disposition filename / filename* from a download response. */
function filenameFromContentDisposition(header, fallback) {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(header)
    || /filename\s*=\s*([^;]+)/i.exec(header);
  if (plain?.[1]) return plain[1].trim();
  return fallback;
}

/** Download DCAA timesheet PDF (submitted or approved). */
export async function downloadTimesheetPdf(timesheetId, filenameHint) {
  const res = await fetch(`/api/timekeeping/timesheets/${timesheetId}/pdf`, {
    credentials: "include",
  });
  if (res.status === 401 || res.status === 403) {
    throw new Error("You do not have permission to download this timesheet.");
  }
  if (!res.ok) throw new Error(await parseError(res));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFromContentDisposition(
    res.headers.get("Content-Disposition"),
    filenameHint || `timesheet-${timesheetId}.pdf`
  );
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Jobs the employee can request (not already assigned). Names only. */
export async function fetchRequestableChargeCodes(search = "") {
  const qs = search.trim()
    ? `?q=${encodeURIComponent(search.trim())}`
    : "";
  const res = await fetch(`/api/timekeeping/charge-codes/requestable${qs}`, {
    credentials: "include",
  });
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function fetchMyChargeCodeAccessRequests(status = "") {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(
    `/api/timekeeping/charge-code-access-requests/mine${qs}`,
    { credentials: "include" }
  );
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function createChargeCodeAccessRequest(chargeCodeId, notes) {
  const res = await fetch("/api/timekeeping/charge-code-access-requests", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      charge_code_id: chargeCodeId,
      notes: notes || null,
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function cancelChargeCodeAccessRequest(requestId) {
  const res = await fetch(
    `/api/timekeeping/charge-code-access-requests/${requestId}/cancel`,
    { method: "POST", credentials: "include" }
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchPendingChargeCodeAccessRequests(scope = "team") {
  const qs = scope === "org" ? "?scope=org" : "";
  const res = await fetch(
    `/api/timekeeping/charge-code-access-requests/pending${qs}`,
    { credentials: "include" }
  );
  const denied = authResult(res);
  if (denied) return { ...denied, data: [] };
  if (!res.ok) throw new Error(await parseError(res));
  return { ok: true, auth: true, data: await res.json() };
}

export async function approveChargeCodeAccessRequest(requestId) {
  const res = await fetch(
    `/api/timekeeping/charge-code-access-requests/${requestId}/approve`,
    { method: "POST", credentials: "include" }
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function denyChargeCodeAccessRequest(requestId, reason) {
  const res = await fetch(
    `/api/timekeeping/charge-code-access-requests/${requestId}/deny`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}
